import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { AppConfigService } from '@core/appconfig.service';
import { UserService } from '@core/user.service';
import { ApiResponse } from '@models/api-response';
import { ISchoolSettings } from '@models/school-settings';
import { IUserSettings } from '@models/user/user-settings';
import { NgbActiveModal, NgbModule, NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { ButtonComponent } from '@shared/button/button.component';
import { Helpers } from '@shared/helpers';
import { PreloaderModule } from '@shared/preloader/preloader.module';
import { ZbpIconComponent } from '@shared/zbp-icon/zbp-icon';
import { zbpIconNames } from '@shared/zbp-icon/zbp-icon-names';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, Observable, Subscription, of } from 'rxjs';
import { distinctUntilChanged, filter, map, mergeMap, skipWhile } from 'rxjs/operators';
import { SchoolsService } from '../../schools/schools.service';
import { userSettingsFieldNames } from './form-field-setup-data';

@Component({
  selector: 'zbp-branded-user-settings',
  templateUrl: './branded-user-settings.component.html',
  styleUrls: ['./branded-user-settings.component.scss'],
  imports: [
    NgbModule,
    ReactiveFormsModule,
    ButtonComponent,
    ZbpIconComponent,
    PreloaderModule
  ]
})
export class BrandedUserSettingsComponent implements OnInit, OnDestroy {
  @Input() isDistrictWide: boolean = false;
  @Input() isSchoolUnderDistrict: boolean = false;
  @Input() formDisabled: boolean = false;

  @Input() set schoolId(value: string) {
    this._schoolId = value;
    this.schoolIdSubject$.next(value);
  }

  form: FormGroup;
  settingsLoaded: boolean = false;
  isSaving: boolean = false;
  isHighlights: boolean = false;
  usesLti: boolean = false;
  usesOneRoster: boolean  = false;
  userIsDistrictAdmin: boolean = false;
  portalNameToDisplay: string = 'My ZB Portal';

  protected readonly userSettingsFieldNames = userSettingsFieldNames;
  protected readonly oneRosterToolTipMessage = Helpers.oneRosterToolTipMessage;
  protected readonly iconNames = zbpIconNames;

  private _schoolId: string;
  private schoolIdSubject$: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  private subscriptions: Subscription[] = [];
  private tooltipTimer: any = null;
  private canShowTooltip: boolean = true;

  constructor(
    private activeModal: NgbActiveModal,
    private schoolsService: SchoolsService,
    private appConfigService: AppConfigService,
    private toastr: ToastrService,
    private userService: UserService) {
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  ngOnInit(): void {
    this.isHighlights = this.appConfigService.isHighlightsPortalUrl;
    this.userIsDistrictAdmin = this.userService.user.isDistrictAdmin;

    if (this.isHighlights) {
      this.portalNameToDisplay = 'My Highlights Portal';
    }

    this.createEmptyForm();

    this.subscriptions.push(
      this.getSettings$.subscribe(),
    );
  }

  getSettings$: Observable<any> = this.schoolIdSubject$
    .pipe(
      skipWhile(value => !value),
      distinctUntilChanged(),
      mergeMap((schoolId) => {
        if (schoolId) {
          this.usesOneRoster = false;
          this.usesLti = false;
          this.form.reset();
          return this.schoolsService.getUserSettings(schoolId);
        }
        return of(null);
      }),
      filter(res => !!res),
      map((res: ApiResponse<ISchoolSettings>) => {
        if (res.success) {
          this.usesOneRoster = res.response.isOneRoster;
          this.usesLti = res.response.isLti;

          this.setupFormData(res.response);

          this.settingsLoaded = true;
        } else {
          this.toastr.error('Error getting settings.');
        }

        return res;
      })
    );

  dismiss(): void {
    this.activeModal.dismiss();
  }

  save(): void {
    if (this._schoolId) {
      this.isSaving = true;
      const canManageParent = !!this.form.value.canManageParent;
      const canManageClassroom = !!(this.form.value.canManageClassroom && !this.usesOneRoster);
      const canManageStudent = !this.usesOneRoster && !this.usesLti && this.form.value.canManageClassroom
        ? this.form.value.canManageClassroom
        : false;

      const values = {
        canChangePassword: this.form.value.canChangePassword,
        canManageParent,
        canManageClassroom,
        canManageSelf: !this.usesOneRoster,
        canManageStudent,
        canChangeStudentPassword: !this.usesLti,
        canManageLicenses: this.form.value.canManageLicenses,
        suppressEmails: this.form.value.suppressEmails,
      } as IUserSettings;

      this.schoolsService.updateUserSettings(this._schoolId, values)
        .subscribe((res: ApiResponse<ISchoolSettings>) => {
          if (res.success) {
            this.toastr.success('Successfully saved user settings.');
            this.activeModal.close(res.response);
          } else {
            this.toastr.error('Error updating settings.');
          }
          this.isSaving = false;
        });
    }
  }

  onTooltipEnter(tooltip: NgbTooltip) {
    if (this.canShowTooltip) {
      tooltip.open();
    }
  }

  onTooltipLeave(tooltip: NgbTooltip) {
    tooltip.close();
    this.canShowTooltip = false;

    this.tooltipTimer = setTimeout(() => {
      this.canShowTooltip = true;
    }, 500); // Delay before it reopens to avoid flickering
  }

  private createEmptyForm() {
    this.form = new FormGroup({
      [userSettingsFieldNames.canChangePassword]: new FormControl(false),
      [userSettingsFieldNames.canManageLicenses]: new FormControl(false),
      [userSettingsFieldNames.canManageSelf]: new FormControl(false),
      [userSettingsFieldNames.canManageClassroom]: new FormControl(false),
      [userSettingsFieldNames.canManageStudent]: new FormControl(false),
      [userSettingsFieldNames.canChangeStudentPassword]: new FormControl(false),
      [userSettingsFieldNames.canManageParent]: new FormControl(false),
      [userSettingsFieldNames.suppressEmails]: new FormControl(false),
    });
  }

  private setupFormData(schoolSettings: ISchoolSettings): void {
    this.form.setValue({
      [userSettingsFieldNames.canManageSelf]: schoolSettings.canManageSelf,
      [userSettingsFieldNames.canChangePassword]: schoolSettings.canChangePassword,
      [userSettingsFieldNames.canManageLicenses]: schoolSettings.canManageLicenses,
      [userSettingsFieldNames.canManageClassroom]: schoolSettings.canManageClassroom,
      [userSettingsFieldNames.canManageStudent]: schoolSettings.canManageStudent,
      [userSettingsFieldNames.canChangeStudentPassword]: schoolSettings.canChangeStudentPassword,
      [userSettingsFieldNames.canManageParent]: schoolSettings.canManageParent,
      [userSettingsFieldNames.suppressEmails]: schoolSettings.suppressEmails ?? false,
    });

    if (this.usesOneRoster) {
      this.form.get(userSettingsFieldNames.canManageClassroom).disable();
    }

    if (this.formDisabled) {
      this.form.disable();
    }
  }
}
